import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonButton,
  IonFooter,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { checkmarkCircleOutline, banOutline } from 'ionicons/icons';
import { ProfileContext } from '../../context/ProfileContext';
import { LoadingScreen } from '../../components/LoadingScreen';
import { isDesktop } from '../../utils/platformUtils';
import { WebLayout } from '../../components/layouts/WebLayout';
import {
  markPaidAndCreateReceipt,
} from '../../functions/Fx_Payment';
import { fx_FB_SendNotification } from '../../functions/Fx_SendPushNotification';
import { PUSH_NOTIFICATION_TYPES } from '../../functions/postActionTypes';
import { UnpaidWerkerSummary, UnpaidJobData, PaymentReceiptJob } from '../../types/payment';
import {
  formatCurrency,
  formatISODateTime,
  formatDate,
  getCurrentTimestamp,
  calculateJobPay,
} from '../../utils/paymentCalculations';

interface LocationState {
  werkerData: UnpaidWerkerSummary;
  selectedJobs: UnpaidJobData[];
}

const PaymentSummaryPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { profile } = useContext(ProfileContext);
  const [present] = useIonAlert();
  const [presentAmountEdit] = useIonAlert();
  const [presentToast] = useIonToast();

  const [werkerData, setWerkerData] = useState<UnpaidWerkerSummary | null>(null);
  const [jobs, setJobs] = useState<(UnpaidJobData & { editedAmount?: number })[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false); // Synchronous guard against double-submission

  const handleCancel = () => {
    history.goBack();
  };

  useEffect(() => {
    if (location.state?.werkerData && location.state?.selectedJobs) {
      setWerkerData(location.state.werkerData);
      setJobs(location.state.selectedJobs.map(job => ({
        ...job,
        editedAmount: undefined, // Will use estimatedAmount unless edited
      })));
    } else {
      // No data, redirect back
      history.replace('/payments');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAmountTap = (jobDocId: string, currentAmount: number) => {
    presentAmountEdit({
      header: 'Edit Amount',
      inputs: [
        {
          name: 'amount',
          type: 'number',
          value: currentAmount,
          placeholder: 'Enter amount',
          min: 0,
          attributes: { inputmode: 'decimal' },
        },
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Save',
          handler: (data) => {
            const parsed = parseFloat(data.amount);
            if (!isNaN(parsed) && parsed >= 0) {
              setJobs(prev => prev.map(j =>
                j.jobDocId === jobDocId
                  ? { ...j, editedAmount: parsed }
                  : j
              ));
            }
          },
        },
      ],
    });
  };

  const totalAmount = jobs.reduce((sum, job) => {
    return sum + (job.editedAmount ?? job.estimatedAmount);
  }, 0);

  const handleMarkAsPaid = () => {
    present({
      header: 'Mark as Paid?',
      message: `This will record a payment of ${formatCurrency(totalAmount)} to ${werkerData?.werkerName}. The jobs will be marked as paid in the system.`,
      buttons: [
        'Cancel',
        {
          text: 'Mark as Paid',
          handler: async () => {
            await processPayment();
          },
        },
      ],
    });  };

  const processPayment = async () => {
    if (isProcessingRef.current) return; // Prevent double-submission
    isProcessingRef.current = true;

    if (!werkerData || !profile.UserId) {
      console.error('Missing required data for payment processing', { 
        hasWerkerData: !!werkerData, 
        profileUserId: profile.UserId 
      });
      presentToast({
        message: 'Unable to process payment. Please try again.',
        duration: 3000,
        position: 'top',
        color: 'danger',
      });
      isProcessingRef.current = false;
      return;
    }

    try {
      setIsProcessing(true);

      const schedulerName = `${profile.FirstName} ${profile.LastName}`.trim();

      // Validate job paths up front
      const jobDocPaths = jobs.map(j => j.jobDocPath).filter(path => path);
      if (jobDocPaths.length !== jobs.length) {
        throw new Error('Some jobs are missing document paths');
      }

      // Build receipt jobs
      const receiptJobs: PaymentReceiptJob[] = jobs.map(job => {
        const calculatedPay = calculateJobPay(
          job.pay,
          job.dateTimeStart,
          job.dateTimeFinish,
          job.notes
        );
        return {
          jobDocId: job.jobDocId,
          uniqueJobId: job.uniqueJobId,
          company: job.company,
          dateTimeStart: job.dateTimeStart,
          dateTimeFinish: job.dateTimeFinish,
          pay: job.pay,
          calculatedAmount: calculatedPay.amount,
          paidAmount: job.editedAmount ?? job.estimatedAmount,
          notes: job.notes,
          isCancelled: job.isCancelled === true,
        };
      });

      // ── ATOMIC STEP: mark jobs paid + create receipt in one batch ──────────
      // If this throws, nothing is written — scheduler can safely retry.
      const receiptId = await markPaidAndCreateReceipt(
        profile.UserId,
        schedulerName,
        werkerData.werkerUid,
        werkerData.werkerName,
        receiptJobs,
        jobDocPaths,
        totalAmount
      );

      // ── NON-CRITICAL: notify the werker (failure won't undo the payment) ───
      try {
        await fx_FB_SendNotification(
          profile.UserId,
          [werkerData.werkerUid],
          PUSH_NOTIFICATION_TYPES.WerkerPaymentReceived,
          { schedulerFName: profile.FirstName, schedulerUid: profile.UserId, receiptId }
        );
      } catch (notifyErr) {
        console.warn('Payment recorded but notification failed:', notifyErr);
      }

      // Show success toast
      presentToast({
        message: 'Payment recorded successfully',
        duration: 3000,
        position: 'top',
        color: 'success',
        icon: checkmarkCircleOutline,
      });

      // Signal that payment just completed for Firestore propagation buffer
      sessionStorage.setItem('paymentJustCompleted', 'true');

      // Navigate to receipt detail page
      history.replace(`/payments/receipt/${receiptId}`);

    } catch (error) {
      console.error('Error processing payment:', error);
      presentToast({
        message: 'Error recording payment. Please try again.',
        duration: 3000,
        position: 'top',
        color: 'danger',
      });
      setIsProcessing(false);
      isProcessingRef.current = false; // Allow retry after error
    }
  };

  // ── Desktop layout ────────────────────────────────────────────────────────
  // Check desktop first so the mobile branch can keep IonPage mounted during loading
  // (prevents page-overlap during the iOS slide transition).
  if (isDesktop()) {
    if (!werkerData || jobs.length === 0) {
      return <LoadingScreen LoadingText="Loading..." />;
    }
    if (isProcessing) {
      return <LoadingScreen LoadingText="Recording payment..." />;
    }
    return (
      <WebLayout title="Payment Summary">
        <div style={{ height: '100%', overflowY: 'auto', padding: '24px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>

            {/* Payment header */}
            <div style={{ padding: '16px', borderBottom: '2px solid var(--ion-color-light)', marginBottom: '16px' }}>
              <IonText color="medium"><p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID FROM</p></IonText>
              <IonText><p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>{profile.FirstName} {profile.LastName}</p></IonText>
              <IonText color="medium"><p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID TO</p></IonText>
              <IonText><p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>{werkerData.werkerName}</p></IonText>
              <IonText color="medium"><p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID DATE</p></IonText>
              <IonText><p style={{ margin: 0, fontWeight: 'bold' }}>{formatDate(getCurrentTimestamp())}</p></IonText>
            </div>

            {/* Line items */}
            <div style={{ marginBottom: '24px' }}>
              <IonText color="medium">
                <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>LINE ITEMS</h3>
              </IonText>
              <IonList>
                {jobs.map((job) => {
                  const displayAmount = job.editedAmount ?? job.estimatedAmount;
                  return (
                    <IonItem key={job.jobDocId} lines="full">
                      <IonLabel>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {job.company}
                          {job.isCancelled && (
                            <IonText color="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'normal' }}>
                              <IonIcon icon={banOutline} style={{ fontSize: '14px' }} />
                              Cancelled
                            </IonText>
                          )}
                        </h3>
                        <p style={{ margin: '2px 0' }}>Start: {formatISODateTime(job.dateTimeStart)}</p>
                        <p style={{ margin: '2px 0' }}>Finish: {formatISODateTime(job.dateTimeFinish)}</p>
                        <p><IonText color="medium" style={{ fontSize: '12px' }}>Pay: {job.pay}</IonText></p>
                      </IonLabel>
                      <IonText
                        slot="end"
                        onClick={() => handleAmountTap(job.jobDocId, displayAmount)}
                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        {formatCurrency(displayAmount)}
                      </IonText>
                    </IonItem>
                  );
                })}
              </IonList>
              <IonText color="medium">
                <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>Click an amount to edit it</p>
              </IonText>
            </div>

            {/* Total + actions */}
            <div style={{
              padding: '16px',
              borderTop: '2px solid var(--ion-color-light)',
              background: 'var(--ion-color-light)',
              borderRadius: '8px',
            }}>
              <div style={{ marginBottom: '16px' }}>
                <IonText color="medium"><p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>TOTAL TO PAY</p></IonText>
                <IonText><h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>{formatCurrency(totalAmount)}</h2></IonText>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <IonButton expand="block" fill="outline" onClick={handleCancel} style={{ flex: 1 }}>Cancel</IonButton>
                <IonButton expand="block" color="success" onClick={handleMarkAsPaid} style={{ flex: 2 }} disabled={isProcessing}>
                  Mark as Paid
                </IonButton>
              </div>
            </div>

          </div>
        </div>
      </WebLayout>
    );
  }

  // ── Mobile layout ─────────────────────────────────────────────────────────
  // Keep IonPage mounted unconditionally so Ionic owns the route transition.
  // The `bckgrnd` class makes IonPage opaque during loading so the previous page
  // doesn't bleed through the transparent LoadingScreen during the slide.
  const mobileLoadingText = !werkerData || jobs.length === 0
    ? 'Loading...'
    : isProcessing
      ? 'Recording payment...'
      : null;

  return (
    <IonPage className={mobileLoadingText ? "bckgrnd" : ""}>
      {mobileLoadingText ? (
        <LoadingScreen LoadingText={mobileLoadingText} />
      ) : (
        <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Payment Summary</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Payment Header */}
        <div style={{ padding: '16px', borderBottom: '2px solid var(--ion-color-light)' }}>
          <IonText color="medium">
            <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID FROM</p>
          </IonText>
          <IonText>
            <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
              {profile.FirstName} {profile.LastName}
            </p>
          </IonText>

          <IonText color="medium">
            <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID TO</p>
          </IonText>
          <IonText>
            <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
              {werkerData.werkerName}
            </p>
          </IonText>

          <IonText color="medium">
            <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>PAID DATE</p>
          </IonText>
          <IonText>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              {formatDate(getCurrentTimestamp())}
            </p>
          </IonText>
        </div>

        {/* Job Line Items */}
        <div style={{ padding: '16px 0' }}>
          <div style={{ padding: '0 16px 8px' }}>
            <IonText color="medium">
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
                LINE ITEMS
              </h3>
            </IonText>
          </div>

          <IonList>
            {jobs.map((job) => {
              const displayAmount = job.editedAmount ?? job.estimatedAmount;

              return (
                <IonItem key={job.jobDocId} lines="full">
                  <IonLabel>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {job.company}
                      {job.isCancelled && (
                        <IonText color="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'normal' }}>
                          <IonIcon icon={banOutline} style={{ fontSize: '14px' }} />
                          Cancelled
                        </IonText>
                      )}
                    </h3>
                    <p style={{ margin: '2px 0' }}>Start: {formatISODateTime(job.dateTimeStart)}</p>
                    <p style={{ margin: '2px 0' }}>Finish: {formatISODateTime(job.dateTimeFinish)}</p>
                    <p>
                      <IonText color="medium" style={{ fontSize: '12px' }}>
                        Pay: {job.pay}
                      </IonText>
                    </p>
                  </IonLabel>

                  <IonText
                    slot="end"
                    onClick={() => handleAmountTap(job.jobDocId, displayAmount)}
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {formatCurrency(displayAmount)}
                  </IonText>
                </IonItem>
              );
            })}
          </IonList>

          <div style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--ion-color-medium)' }}>
            <IonText>
              <p style={{ margin: 0 }}>
                Tap an amount to edit it
              </p>
            </IonText>
          </div>
        </div>
      </IonContent>

      <IonFooter>
        <div
          style={{
            padding: '16px',
            borderTop: '2px solid var(--ion-color-light)',
            backgroundColor: 'var(--ion-color-light)',
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <IonText color="medium">
              <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>TOTAL TO PAY</p>
            </IonText>
            <IonText>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                {formatCurrency(totalAmount)}
              </h2>
            </IonText>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <IonButton
              expand="block"
              fill="outline"
              onClick={handleCancel}
              style={{ flex: 1 }}
            >
              Cancel
            </IonButton>
            <IonButton
              expand="block"
              color="success"
              onClick={handleMarkAsPaid}
              style={{ flex: 2 }}
              disabled={isProcessing}
            >
              Mark as Paid
            </IonButton>
          </div>
        </div>
      </IonFooter>
        </>
      )}
    </IonPage>
  );
};

export default PaymentSummaryPage;
